import { translate, type WashedLocale } from '@washed/i18n';
import { PageShell } from './PageShell.js';
import styles from './SeoIntentPage.module.css';

const SITE = 'https://washedafrica.com';

export type SeoAudienceKey = 'student-laundry' | 'young-professional-laundry';

type AudienceCopy = {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  serviceType: string;
  sections: { heading: string; paragraphs: string[] }[];
  faqs: { q: string; a: string }[];
  related: { href: string; label: string }[];
};

const PATH_TO_AUDIENCE: Record<string, SeoAudienceKey> = {
  '/lessive-etudiant-lome': 'student-laundry',
  '/student-laundry-lome': 'student-laundry',
  '/lessive-jeune-professionnel-lome': 'young-professional-laundry',
  '/laundry-young-professionals-lome': 'young-professional-laundry',
};

export function seoAudienceKeyFromPath(path: string): SeoAudienceKey | null {
  const normalized = path.replace(/\/+$/u, '') || '/';
  return PATH_TO_AUDIENCE[normalized] ?? null;
}

const COPY: Record<SeoAudienceKey, Record<WashedLocale, AudienceCopy>> = {
  'student-laundry': {
    fr: {
      slug: 'lessive-etudiant-lome',
      eyebrow: 'Washed · Étudiants',
      title: 'Service de lessive pour étudiants à Lomé',
      intro:
        'Pour un étudiant qui manque de temps, d’équipement ou simplement d’envie de passer son week-end sur la lessive, Washed propose une aide récurrente à domicile sans transformer le besoin en contrat de ménage complet.',
      serviceType: 'Service de lessive à domicile pour étudiants à Lomé',
      sections: [
        {
          heading: 'Déléguer la lessive, pas toute sa vie domestique',
          paragraphs: [
            'Washed est centré sur le linge. Une laveuse vient au domicile, lave sur place avec l’eau et le savon du foyer, puis le service revient selon la formule choisie.',
            'Ce format peut convenir à un étudiant qui veut externaliser une tâche précise sans payer pour du ménage, de la cuisine ou d’autres prestations qu’il ne recherche pas.',
          ],
        },
        {
          heading: 'Un budget mensuel connu à l’avance',
          paragraphs: [
            'Les formules Solo actuellement publiées commencent à 4 500 XOF par mois pour une visite et 8 500 XOF pour deux visites.',
            'Chaque visite couvre la quantité raisonnable de linge du foyer enregistré pour la période de service ; un volume clairement excessif est signalé pour examen. Le paiement local se fait par mobile money via Mixx by Yas ou Flooz.',
          ],
        },
        {
          heading: 'Partout à Lomé',
          paragraphs: [translate('marketing.seo.coverage.body', 'fr')],
        },
      ],
      faqs: [
        {
          q: 'Quel est le tarif minimum pour un étudiant ?',
          a: 'La formule Solo 1× commence à 4 500 XOF par mois pour une visite.',
        },
        {
          q: 'Le linge est-il récupéré ?',
          a: 'Non. La laveuse vient au domicile et lave sur place.',
        },
        {
          q: 'Faut-il avoir une machine à laver ?',
          a: 'Non. Le service Washed décrit ici est un service de lavage manuel à domicile avec l’eau et le savon du foyer.',
        },
      ],
      related: [
        { href: '/prix-laveuse-lome', label: 'Voir les tarifs Washed' },
        { href: '/pas-le-temps-lessive-lome', label: 'Quand on manque de temps pour la lessive' },
        { href: '/zones', label: translate('marketing.seo.zones.link', 'fr') },
      ],
    },
    en: {
      slug: 'student-laundry-lome',
      eyebrow: 'Washed · Students',
      title: 'Laundry service for students in Lomé',
      intro:
        'For students who are short on time, equipment or simply do not want to spend the weekend doing laundry, Washed offers recurring in-home help without turning the need into a full housekeeping arrangement.',
      serviceType: 'In-home laundry service for students in Lomé',
      sections: [
        {
          heading: 'Delegate laundry, not every household task',
          paragraphs: [
            'Washed focuses on laundry. A washerwoman comes to the home, washes on-site using the household’s water and soap, and returns according to the selected plan.',
            'That can suit a student who wants to outsource one specific chore without paying for cleaning, cooking or other domestic services they do not need.',
          ],
        },
        {
          heading: 'A monthly price known in advance',
          paragraphs: [
            'Washed currently publishes Solo plans starting at 4,500 XOF per month for one visit and 8,500 XOF for two visits.',
            'Each visit covers a reasonable amount of laundry for the registered household and service period; clearly excessive volume is flagged for review. Local payment is by mobile money through Mixx by Yas or Flooz.',
          ],
        },
        {
          heading: 'Across Lomé',
          paragraphs: [translate('marketing.seo.coverage.body', 'en')],
        },
      ],
      faqs: [
        {
          q: 'What is the cheapest plan for a student?',
          a: 'The Solo 1× plan starts at 4,500 XOF per month for one visit.',
        },
        {
          q: 'Is the laundry picked up?',
          a: 'No. The washerwoman comes to the home and washes on-site.',
        },
        {
          q: 'Do I need a washing machine?',
          a: 'No. The Washed service described here is manual in-home washing using the household’s water and soap.',
        },
      ],
      related: [
        { href: '/en/laundry-prices-lome', label: 'See Washed pricing' },
        {
          href: '/en/laundry-for-busy-people-lome',
          label: 'When you do not have time for laundry',
        },
        { href: '/en/zones', label: translate('marketing.seo.zones.link', 'en') },
      ],
    },
  },
  'young-professional-laundry': {
    fr: {
      slug: 'lessive-jeune-professionnel-lome',
      eyebrow: 'Washed · Jeunes actifs',
      title: 'Lessive à domicile pour jeunes professionnels à Lomé',
      intro:
        'Quand les journées de travail sont longues, la lessive devient facilement une tâche repoussée au soir ou au week-end. Washed organise une laveuse récurrente à domicile pour rendre cette routine plus prévisible.',
      serviceType: 'Service de lessive pour jeunes professionnels à Lomé',
      sections: [
        {
          heading: 'Récupérer du temps sans recruter une aide à temps plein',
          paragraphs: [
            'Le service Washed se concentre sur le linge : la laveuse intervient au domicile, le foyer fournit l’eau et le savon et Washed gère la mise en relation, le planning et le paiement.',
            'Cela répond à un besoin plus étroit qu’une aide ménagère générale et évite d’organiser des tâches domestiques dont le foyer n’a pas nécessairement besoin.',
          ],
        },
        {
          heading: 'Une routine mensuelle plutôt qu’une recherche de dernière minute',
          paragraphs: [
            'Les formules individuelles prévoient une ou deux visites par mois. La laveuse habituelle est réassignée par défaut afin de construire une relation régulière dans le temps.',
            'Si elle est indisponible ou qu’un changement est nécessaire, Washed peut organiser un remplacement.',
          ],
        },
        {
          heading: 'Tarifs simples',
          paragraphs: [
            'Solo 1× coûte actuellement 4 500 XOF par mois pour une visite et Solo 2× 8 500 XOF pour deux visites.',
            'L’inscription se fait avec votre numéro et votre quartier.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Washed convient-il si je travaille toute la semaine ?',
          a: 'Le service est précisément conçu pour déléguer une tâche récurrente, avec des visites planifiées plutôt qu’une recherche de dernière minute.',
        },
        {
          q: 'Puis-je avoir la même laveuse ?',
          a: 'La laveuse habituelle est réassignée par défaut, avec remplacement possible si nécessaire.',
        },
        {
          q: 'Combien coûte deux visites par mois ?',
          a: 'La formule Solo 2× est actuellement publiée à 8 500 XOF par mois.',
        },
      ],
      related: [
        { href: '/abonnement-lessive-lome', label: 'Comprendre l’abonnement mensuel' },
        { href: '/trouver-laveuse-fiable-lome', label: 'Trouver une laveuse fiable' },
        { href: '/laveuse-ou-aide-menagere', label: 'Laveuse ou aide ménagère ?' },
      ],
    },
    en: {
      slug: 'laundry-young-professionals-lome',
      eyebrow: 'Washed · Young professionals',
      title: 'In-home laundry for young professionals in Lomé',
      intro:
        'When workdays are long, laundry is easily pushed into evenings and weekends. Washed organizes recurring in-home washerwoman visits so the routine becomes more predictable.',
      serviceType: 'Laundry service for young professionals in Lomé',
      sections: [
        {
          heading: 'Get time back without hiring full-time domestic help',
          paragraphs: [
            'Washed focuses on laundry: the washerwoman works at the home, the household provides water and soap, and Washed coordinates the match, schedule and payment.',
            'That solves a narrower problem than general housekeeping and avoids organizing domestic tasks the household may not need.',
          ],
        },
        {
          heading: 'A monthly routine instead of last-minute searching',
          paragraphs: [
            'Individual plans provide one or two visits per month. The regular washerwoman is reassigned by default so a recurring relationship can build over time.',
            'If she is unavailable or a change is needed, Washed can arrange a replacement.',
          ],
        },
        {
          heading: 'Simple published pricing',
          paragraphs: [
            'Solo 1× currently costs 4,500 XOF per month for one visit and Solo 2× 8,500 XOF for two visits.',
            'You sign up with your number and neighbourhood.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Does Washed make sense if I work all week?',
          a: 'The service is designed to delegate a recurring chore through planned visits rather than last-minute searching.',
        },
        {
          q: 'Can I keep the same washerwoman?',
          a: 'The regular washerwoman is reassigned by default, with a replacement available when needed.',
        },
        {
          q: 'How much are two visits per month?',
          a: 'The Solo 2× plan is currently published at 8,500 XOF per month.',
        },
      ],
      related: [
        { href: '/en/laundry-subscription-lome', label: 'Understand the monthly subscription' },
        { href: '/en/reliable-washerwoman-lome', label: 'Find a reliable washerwoman' },
        { href: '/en/washerwoman-vs-housekeeper-lome', label: 'Washerwoman or housekeeper?' },
      ],
    },
  },
};

interface SeoAudiencePageProps {
  intent: SeoAudienceKey;
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

export function SeoAudiencePage({ intent, locale, onLocaleChange }: SeoAudiencePageProps) {
  const c = COPY[intent][locale];
  const href = locale === 'en' ? `/en/${c.slug}` : `/${c.slug}`;
  const pageUrl = `${SITE}${href}`;
  const structured = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: c.title,
        description: c.intro,
        inLanguage: locale,
        isPartOf: { '@id': `${SITE}/#website` },
        about: { '@id': `${SITE}/#organization` },
      },
      {
        '@type': 'Service',
        '@id': `${pageUrl}#service`,
        name: c.serviceType,
        serviceType: c.serviceType,
        provider: { '@id': `${SITE}/#organization` },
        areaServed: {
          '@type': 'City',
          name: 'Lomé',
          containedInPlace: { '@type': 'Country', name: 'Togo' },
        },
        url: pageUrl,
        description: c.intro,
      },
    ],
  };

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      eyebrow={c.eyebrow}
      title={c.title}
      intro={c.intro}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }}
      />

      <div className={styles.article}>
        {c.sections.map((section) => (
          <section key={section.heading} className={styles.section}>
            <h2>{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>

      <section className={styles.faqBlock} aria-labelledby="audience-faq">
        <h2 id="audience-faq">
          {locale === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
        </h2>
        <dl className={styles.faqList}>
          {c.faqs.map((item) => (
            <div key={item.q} className={styles.faqItem}>
              <dt>{item.q}</dt>
              <dd>{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <aside
        className={styles.related}
        aria-label={locale === 'fr' ? 'Guides liés' : 'Related guides'}
      >
        <h2>{locale === 'fr' ? 'Guides liés' : 'Related guides'}</h2>
        <div className={styles.relatedGrid}>
          {c.related.map((link) => (
            <a key={link.href} href={link.href} className={styles.relatedCard}>
              <strong>{link.label}</strong>
              <span>{locale === 'fr' ? 'Lire' : 'Read'} →</span>
            </a>
          ))}
        </div>
      </aside>
    </PageShell>
  );
}
